import { Routes, Route } from 'react-router-dom';
import Home from './pages/Home.jsx';
import SignIn from './pages/SignIn.jsx';
import SignUp from './pages/SignUp.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import Profile from './pages/Profile.jsx';
import HubViewer from './pages/HubViewer.jsx';
import NotFound from './pages/NotFound.jsx';
import AISettings from './pages/AISettings.jsx';
import RequireAuth from './components/RequireAuth.jsx';
import AiPanel from './components/AiPanel.jsx';

// Generic hub pages (config-driven)
import Flashcards from './pages/hub/Flashcards.jsx';
import PracticeTest from './pages/hub/PracticeTest.jsx';
import ObjectiveTests from './pages/hub/ObjectiveTests.jsx';
import Domains from './pages/hub/Domains.jsx';
import LegacyContentPage from './pages/hub/LegacyContentPage.jsx';

// CompTIA Security+ native React narrative pages
import SecPlusOverview from './pages/hub/secplus/Overview.jsx';
import SecPlusDomains from './pages/hub/secplus/Domains.jsx';
import SecPlusStudyPlan from './pages/hub/secplus/StudyPlan.jsx';

// Hub configs
import secplusConfig from './data/hubs/comptia-secplus/config.js';
import awsAifConfig from './data/hubs/aws-ai-practitioner/config.js';
import azAi901Config from './data/hubs/azure-ai-901/config.js';
import gcpCeConfig from './data/hubs/google-cloud-engineer/config.js';

function gated(node) {
  return <RequireAuth>{node}</RequireAuth>;
}

/**
 * Build the 8 standard hub routes for a given config.
 * `overrides` lets callers swap in native React components for specific pages.
 * Anything not overridden falls back to LegacyContentPage (static HTML bridge).
 */
function hubRoutes(config, overrides = {}) {
  const slug = config.slug;
  const pages = [
    ['', 'index'],
    ['/domains', 'domains'],
    ['/study-plan', 'study-plan'],
    ['/flashcards', 'flashcards'],
    ['/practice-test', 'practice-test'],
    ['/objective-tests', 'objective-tests'],
    ['/cheat-sheet', 'cheat-sheet'],
    ['/resources', 'resources'],
  ];
  return pages.map(([path, page]) => {
    const element = overrides[page] || <LegacyContentPage config={config} page={page} />;
    return (
      <Route
        key={`${slug}${path}`}
        path={`/hub/${slug}${path}`}
        element={gated(element)}
      />
    );
  });
}

export default function App() {
  return (
    <>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <Profile />
          </RequireAuth>
        }
      />

      {/* CompTIA Security+ — full native React */}
      {hubRoutes(secplusConfig, {
        'index': <SecPlusOverview />,
        'domains': <SecPlusDomains />,
        'study-plan': <SecPlusStudyPlan />,
        'flashcards': <Flashcards config={secplusConfig} />,
        'practice-test': <PracticeTest config={secplusConfig} />,
        'objective-tests': <ObjectiveTests config={secplusConfig} />,
      })}

      {/* AWS AI Practitioner — interactive React, narrative via legacy bridge */}
      {hubRoutes(awsAifConfig, {
        'domains': <Domains config={awsAifConfig} />,
        'flashcards': <Flashcards config={awsAifConfig} />,
        'practice-test': <PracticeTest config={awsAifConfig} />,
        'objective-tests': <ObjectiveTests config={awsAifConfig} />,
      })}

      {/* Azure AI Fundamentals — interactive React, narrative via legacy bridge */}
      {hubRoutes(azAi901Config, {
        'domains': <Domains config={azAi901Config} />,
        'flashcards': <Flashcards config={azAi901Config} />,
        'practice-test': <PracticeTest config={azAi901Config} />,
        'objective-tests': <ObjectiveTests config={azAi901Config} />,
      })}

      {/* Google Cloud Engineer — interactive React, narrative via legacy bridge */}
      {hubRoutes(gcpCeConfig, {
        'domains': <Domains config={gcpCeConfig} />,
        'flashcards': <Flashcards config={gcpCeConfig} />,
        'practice-test': <PracticeTest config={gcpCeConfig} />,
        'objective-tests': <ObjectiveTests config={gcpCeConfig} />,
      })}

      {/* Fallback: any other hub goes through the legacy iframe bridge */}
      <Route
        path="/hub/:slug"
        element={
          <RequireAuth>
            <HubViewer />
          </RequireAuth>
        }
      />
      <Route path="/ai-settings" element={gated(<AISettings />)} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    <AiPanel />
    </>
  );
}
